import './ArticleIdeaList.scss';

import React, { FC, useContext, useState } from 'react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import { GeneratedArticle, GeneratedTopicModel } from 'interfaces/GeneratedTopicModel';
import { LogLevel } from 'models/LogLevel';
import { Button, Form, Table } from 'react-bootstrap';

interface ArticleIdeaListProps {
  onSelectArticleIdeas: (articleIdeas: GeneratedTopicModel[]) => void,
  articleIdeas: GeneratedTopicModel[],
  isLoading: boolean,
  setIsLoading: (isLoading: boolean) => void,
  activeStep: number;
}

const thisStepNumber = 4;

const ArticleIdeaList: FC<ArticleIdeaListProps> = ({ onSelectArticleIdeas, articleIdeas, isLoading, setIsLoading, activeStep }) => {
  const { sendLog, notify } = useContext(AppContext);
  const [selectedArticleIdeaIds, setSelectedArticleIdeaIds] = useState<Set<number>>(new Set());

  const handleSelectArticleIdeas = async () => {
    setIsLoading(true);
    const filteredThemes: GeneratedTopicModel[] = [];
    articleIdeas.forEach((themeIdea) => {
      const filteredArticles = themeIdea.articles.filter(article => selectedArticleIdeaIds.has(article.id));
      if (filteredArticles.length > 0) {
        filteredThemes.push({
          ...themeIdea,
          articles: filteredArticles,
        });
      }
    });

    try {
      if (filteredThemes.length === 0) {
        sendLog('No article ideas selected for content plan', LogLevel.warn);
        notify('No article ideas selected for content plan', NotificationType.Warning);
      }
      else {
        await onSelectArticleIdeas(filteredThemes);
        sendLog('triggered select article ideas', LogLevel.info, filteredThemes);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleArticleIdeaSelected = (articleIdeaId: number, checked: boolean) => {
    setSelectedArticleIdeaIds((prev: Set<number>) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(articleIdeaId);
      } else {
        newSet.delete(articleIdeaId);
      }
      return newSet;
    });
    sendLog(`The article idea id ${articleIdeaId} was checked: ${checked}`, LogLevel.info);
  }

  const renderArticleIdeas = (articles: GeneratedArticle[]) => (<Table striped>
    <thead>
      <tr>
        <th aria-label='select article idea'>Select</th>
        <th>Article Idea</th>
        <th>Planned Date</th>
        <th>Category</th>
        <th>SEO Keyword</th>
        <th>Search Volume</th>
        <th>Difficulty</th>
      </tr>
    </thead>
    <tbody>
      {articles.map((article) => (
        <tr key={article.id}>
          <td>
            <Form.Check
              type="checkbox"
              id={`article-idea-${article.id}`}
              key={article.id}
              aria-label="Select article idea"
              label=""
              checked={selectedArticleIdeaIds.has(article.id)}
              onChange={(e) => handleArticleIdeaSelected(article.id, e.target.checked)}
            />
          </td>
          <td><Form.Label htmlFor={`article-idea-${article.id}`}>{article.title}</Form.Label></td>
          <td>{article.plannedDate.toString()}</td>
          <td>{article.categoryName}</td>
          <td>{article.keyword}</td>
          <td>{article.searchVolume}</td>
          <td>{article.difficulty}</td>
        </tr>
      ))}
    </tbody>
  </Table>)

  const renderThemes = () => articleIdeas.map((themeIdea, _) => (
    <div className='theme-article-idea' key={themeIdea.id}>
      <h4>{themeIdea.theme}</h4>
      <div className="theme-article-description">
        {themeIdea.themeDescription}
      </div>
      {renderArticleIdeas(themeIdea.articles)}
    </div>
  ));

  return (
    <div className="KeywordList" style={{ display: activeStep === thisStepNumber ? 'block' : 'none' }}>
      <div className="identified-keywords" style={{ display: articleIdeas.length > 0 ? 'block' : 'none' }}>
        <Form>
          <h2>Select Article Ideas to Add To Your Content Plan:</h2>
          {renderThemes()}
          <div className="d-flex justify-content-end">
            <Button variant="primary" onClick={handleSelectArticleIdeas} disabled={isLoading}>
              <i className="bi bi-plus-circle" />
              Save Article Ideas
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}

export default ArticleIdeaList;
